import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, IconButton, Link, TextField, Typography } from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../ui/colors";
import { inputStyles } from "../../../ui/styles/inputStyles";
import { checkEmailAvailability } from "../../helpers/checkEmailAvailability";
import { PasswordStrengthChecker } from "../../utils/PasswordStrengthChecker";

export const AccountInformationForm = ({ control, errors }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const navigate = useNavigate();

  const passwordValue = useWatch({ control, name: "password" });

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          color: colors.gray.base,
          fontWeight: 600,
          fontSize: { xs: "18px", md: "20px" },
        }}
      >
        Set your keys to enter!
      </Typography>

      {/* First + Last Name */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 5 },
          mb: 4,
        }}
      >
        <Controller
          name="firstName"
          control={control}
          rules={{ required: "First name is required" }}
          render={({ field }) => (
            <TextField {...field} fullWidth label="Name" error={!!errors.firstName} helperText={errors.firstName?.message} sx={inputStyles} />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{ required: "Last name is required" }}
          render={({ field }) => (
            <TextField {...field} fullWidth label="Last Name" error={!!errors.lastName} helperText={errors.lastName?.message} sx={inputStyles} />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Controller
          name="alias"
          control={control}
          rules={{
            maxLength: {
              value: 80,
              message: "Alias must be at most 80 characters",
            },
          }}
          render={({ field }) => <TextField {...field} fullWidth label="Alias" error={!!errors.alias} helperText={errors.alias?.message} sx={inputStyles} />}
        />
      </Box>

      {/* Email */}
      <Box sx={{ mb: 3 }}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
            validate: async (value) => {
              if (!value) {
                setEmailError(null);
                return true; // If no value, let the required validation handle it
              }

              const available = await checkEmailAvailability(value);

              // If available is false, the email is not available
              if (available === false) {
                setEmailError(value); // Store the email for the link
                return "EMAIL_IN_USE"; // Return a string that identifies the error
              }

              setEmailError(null);
              return true;
            },
          }}
          render={({ field }) => {
            const isEmailInUse = errors.email?.message === "EMAIL_IN_USE";
            return (
              <TextField
                {...field}
                fullWidth
                label="Email Address"
                error={!!errors.email}
                helperText={
                  isEmailInUse && emailError ? (
                    <span>
                      This email is already in use,{" "}
                      <Link
                        component="button"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/signin?email=${encodeURIComponent(emailError)}`);
                        }}
                        sx={{
                          color: colors.info.base,
                          textDecoration: "underline",
                          fontSize: "inherit",
                          fontWeight: 500,
                          cursor: "pointer",
                          border: "none",
                          background: "none",
                          padding: 0,
                        }}
                      >
                        Sign in with this email instead
                      </Link>
                    </span>
                  ) : typeof errors.email?.message === "string" ? (
                    errors.email?.message
                  ) : (
                    <Box component="span">{errors.email?.message}</Box>
                  )
                }
                sx={{ mb: 2, ...inputStyles }}
              />
            );
          }}
        />
      </Box>

      {/* Password + Confirm Password */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: colors.gray[400] }}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              sx={inputStyles}
            />
          )}
        />

        <Box sx={{ ml: 1, mb: 1 }}>
          <PasswordStrengthChecker password={passwordValue || ""} />
        </Box>

        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: "Please confirm your password",
            validate: (value, formValues) => value === formValues.password || "Passwords do not match",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} sx={{ color: colors.gray[400] }}>
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              sx={inputStyles}
            />
          )}
        />
      </Box>
    </Box>
  );
};
